import styled from '@emotion/styled';
import type { Review } from '..';
import { StarIcon } from '../../../assets/icons/StarIcon';
import { VerifiedIcon } from '../../../assets/icons/VerifiedIcon';
import { ChevronIcon } from '../../../assets/icons/ChevronIcon';
import { formatRelativeTime } from '../../../utils/dateFormat';
import { useState } from 'react';
import { useMediaQuery } from 'react-responsive';

const Container = styled.div`
  margin-bottom: 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    margin-top: 12px;
  }
`;

const VerifiedPurchase = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #8a8a8a;
  font-size: 12px;
`;

const DateText = styled.span`
  color: var(--gray-500, #8a8a8a);
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  line-height: 1.4;
  font-weight: 400;
  line-height: normal;
`;

const ContentContainer = styled.div<{ hasImages: boolean }>`
  padding: 16px;
  background: #f7f7f7;
  border-radius: 16px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 12px;

  ${(props) => !props.hasImages && 'flex-direction: column;'}
`;

const ImageSection = styled.div`
  position: relative;
  flex: 0 0 208px;
  width: 208px;
  height: 240px;
`;

const MainImage = styled.div<{ url: string }>`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === 'left' ? 'left: 4px;' : 'right: 4px;')}
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(247, 247, 249, 0.2);
  backdrop-filter: blur(10.76px);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(247, 247, 249, 0.4);
  }

  svg {
    width: 16px;
    height: 16px;

    path {
      stroke: #f7f7f9 !important;
    }
  }
`;

const TextSection = styled.div<{ hasImages: boolean }>`
  flex: 1;
  ${(props) => !props.hasImages && 'flex-direction: column;'}
`;

const Title = styled.div`
  font-family: 'Plus Jakarta Sans';
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: #2a2a2a;
  margin: 0 0 8px 0;
  list-spacing: 8px;
`;

const Content = styled.p`
  font-family: 'Plus Jakarta Sans';
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #474747;
  margin: 0;
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  width: 208px;
  margin-top: 16px;
`;

const ThumbnailImage = styled.div<{ url: string; isActive: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  cursor: pointer;
  transition: transform 0.2s ease;
  border: ${(props) => (props.isActive ? '1.6px solid #6f3fd7' : 'none')};

  &:hover {
    transform: scale(1.05);
  }

  ${(props) =>
    props.isActive &&
    `
    position: relative;
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(111, 63, 215, 0.3);
      border-radius: 8px;
    }
  `}
`;

const Divider = styled.div`
  width: 100%;
  margin: 12px 0;
  background: #dcdcdc;
  height: 1px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UserAvatar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #8a8a8a;
`;

const UserName = styled.span`
  font-size: 12px;
  color: #8a8a8a;
  font-weight: 400;
`;

const StarRating = styled.div`
  display: flex;
  gap: 4px;
`;

const SkinInfoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const SkinInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SkinInfoLabel = styled.span`
  color: #8a8a8a;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const SkinInfoValue = styled.span`
  color: var(--Text-in-light-bg-25, #8a8a8a);
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const MobileContentContainer = styled.div`
  padding: 12px;
  background: #f7f7f7;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 12px;
`;

const MobileImageSection = styled.div`
  position: relative;
  width: 100%;
  height: 358px;
`;

const MobileMainImage = styled.div<{ url: string }>`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const MobileNavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.direction === 'left' ? 'left: 8px;' : 'right: 8px;')}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(247, 247, 249, 0.2);
  backdrop-filter: blur(16px);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(247, 247, 249, 0.4);
  }

  svg {
    width: 24px;
    height: 24px;

    path {
      stroke: #f7f7f9 !important;
      stroke-width: 1.6px;
    }
  }
`;

const PageIndicatorContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 8px;
  border-radius: 23px;
  background: rgba(247, 247, 249, 0.2);
  backdrop-filter: blur(16px);
`;

const PageIndicator = styled.div<{ isActive: boolean }>`
  width: ${(props) => (props.isActive ? '20px' : '4px')};
  height: 4px;
  border-radius: 4px;
  background-color: ${(props) => (props.isActive ? '#ffffff' : '#c4c4c4')};
  transition: all 0.2s ease;
`;

const MobileTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MobileTitle = styled.h3`
  font-family: 'Plus Jakarta Sans';
  font-size: 18px;
  font-weight: 500;
  line-height: 1.4;
  color: #2a2a2a;
  margin: 0;
`;

const MobileContent = styled.p`
  font-family: 'Plus Jakarta Sans';
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: #474747;
  margin: 0;
`;

const MobileFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

const MobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MobileUserAvatar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #fff3ee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #8a8a8a;
  overflow: hidden;
`;

const MobileUserName = styled.span`
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  color: #8a8a8a;
  font-weight: 400;
`;

const MobileStarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const MobileDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #f7f7f9;
  margin: 12px 0;
`;

const MobileSkinInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MobileSkinInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MobileSkinInfoLabel = styled.span`
  color: #8a8a8a;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-weight: 400;
`;

const MobileSkinInfoValue = styled.span`
  color: #8a8a8a;
  font-family: 'Plus Jakarta Sans';
  font-size: 12px;
  font-weight: 400;
`;

interface ReviewCardProps {
  review: Review;
  onLikeClick?: (reviewId: string) => void;
  onImageClick?: () => void;
  isLastInColumn?: boolean;
}

export const ReviewCard = ({ review, onLikeClick, onImageClick, isLastInColumn = false }: ReviewCardProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const hasImages = Boolean(review.images && review.images.length > 0);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const handlePrevImage = () => {
    if (hasImages && review.images) {
      setActiveImageIndex((prev) => (prev === 0 ? review.images!.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (hasImages && review.images) {
      setActiveImageIndex((prev) => (prev === review.images!.length - 1 ? 0 : prev + 1));
    }
  };

  const handleThumbnailClick = (index: number) => {
    setActiveImageIndex(index);
  };

  return (
    <Container>
      <Header>
        <VerifiedPurchase>
          <VerifiedIcon />
          Verified purchase
        </VerifiedPurchase>
        <DateText>{formatRelativeTime(review.createdAt)}</DateText>
      </Header>

      {isMobile ? (
        <>
          <MobileContentContainer>
            {hasImages && review.images && (
              <MobileImageSection>
                <MobileMainImage url={review.images[activeImageIndex].url}>
                  {review.images.length > 1 && (
                    <>
                      <MobileNavigationButton direction="left" onClick={handlePrevImage}>
                        <ChevronIcon direction="left" />
                      </MobileNavigationButton>
                      <MobileNavigationButton direction="right" onClick={handleNextImage}>
                        <ChevronIcon direction="right" />
                      </MobileNavigationButton>
                      <PageIndicatorContainer>
                        {review.images.map((_, index) => (
                          <PageIndicator key={index} isActive={index === activeImageIndex} />
                        ))}
                      </PageIndicatorContainer>
                    </>
                  )}
                </MobileMainImage>
              </MobileImageSection>
            )}

            <MobileTextSection>
              <MobileTitle>{review.title}</MobileTitle>
              <MobileContent>{review.content}</MobileContent>
            </MobileTextSection>
          </MobileContentContainer>

          <MobileFooter>
            <MobileUserInfo>
              <MobileUserAvatar>{review.author.name.charAt(0).toUpperCase()}</MobileUserAvatar>
              <MobileUserName>{review.author.name}</MobileUserName>
              <MobileStarRating>
                {[...Array(review.rating)].map((_, index) => (
                  <StarIcon width={16} height={16} key={index} />
                ))}
              </MobileStarRating>
            </MobileUserInfo>
          </MobileFooter>

          <MobileDivider />

          <MobileSkinInfoContainer>
            <MobileSkinInfoItem>
              <MobileSkinInfoLabel>Skin type:</MobileSkinInfoLabel>
              <MobileSkinInfoValue>{review.skinType.join(', ')}</MobileSkinInfoValue>
            </MobileSkinInfoItem>
            {review.skinConcern && (
              <MobileSkinInfoItem>
                <MobileSkinInfoLabel>Skin concern:</MobileSkinInfoLabel>
                <MobileSkinInfoValue>{review.skinConcern.join(', ')}</MobileSkinInfoValue>
              </MobileSkinInfoItem>
            )}
          </MobileSkinInfoContainer>
        </>
      ) : (
        <>
          <ContentContainer hasImages={hasImages}>
            {hasImages && review.images && (
              <div>
                <ImageSection>
                  <MainImage url={review.images[activeImageIndex].url}>
                    {review.images.length > 1 && (
                      <>
                        <NavigationButton direction="left" onClick={handlePrevImage}>
                          <ChevronIcon direction="left" />
                        </NavigationButton>
                        <NavigationButton direction="right" onClick={handleNextImage}>
                          <ChevronIcon direction="right" />
                        </NavigationButton>
                      </>
                    )}
                  </MainImage>
                </ImageSection>
                {review.images.length > 1 && (
                  <ThumbnailRow>
                    {review.images.slice(0, 3).map((image, index) => (
                      <ThumbnailImage
                        key={image.id}
                        url={image.url}
                        isActive={index === activeImageIndex}
                        onClick={() => handleThumbnailClick(index)}
                      />
                    ))}
                  </ThumbnailRow>
                )}
              </div>
            )}

            <TextSection hasImages={hasImages}>
              <Title>{review.title}</Title>
              <Content>{review.content}</Content>
            </TextSection>
          </ContentContainer>

          <Footer>
            <UserInfo>
              <UserAvatar>{review.author.name.charAt(0).toUpperCase()}</UserAvatar>
              <UserName>{review.author.name}</UserName>
              <StarRating>
                {[...Array(review.rating)].map((_, index) => (
                  <StarIcon width={16} height={16} key={index} />
                ))}
              </StarRating>
            </UserInfo>
          </Footer>

          <SkinInfoContainer>
            <SkinInfoItem>
              <SkinInfoLabel>Skin type:</SkinInfoLabel>
              <SkinInfoValue>{review.skinType.join(', ')}</SkinInfoValue>
            </SkinInfoItem>
            {review.skinConcern && (
              <SkinInfoItem>
                <SkinInfoLabel>Skin concern:</SkinInfoLabel>
                <SkinInfoValue>{review.skinConcern.join(', ')}</SkinInfoValue>
              </SkinInfoItem>
            )}
          </SkinInfoContainer>
        </>
      )}

      {!isLastInColumn && <Divider />}
    </Container>
  );
};

export default ReviewCard;
